import { useEffect, useRef, useState } from 'react';
import SlideEditorCSS from '../../assets/SlideEditorCSS.jsx';
// import ProjectAnalyticsDashboard from './ProjectAnalyticsDashboard';
import SlideTemplateEditor from './SlideTemplateEditor';
import SlideEditorProvider, {
  Image,
  useSlideEditorContext
} from 'providers/SlideEditorProvider';
import projectService from 'services/project';
import pitchDeckService from 'services/pitchdeck';
import {
  SET_PITCH_DECK_DATA,
  SET_PROJECT_DATA,
  SET_SLIDES
} from 'reducers/SlideEditorReducer';
import Konva from 'konva';
import { useNavigate, useParams } from 'react-router-dom';
import NPSpinner from 'components/base/NPSpinner';
import slidesService from 'services/slide';

export interface Text {
  x: number;
  y: number;
  fontStyle: string;
  size: number;
  color: string;
  text: string;
}

export interface SlidesResponse {
  slideId: string;
  pitchDeckId: string;
  pitchDeck: string | null;
  contentInnerHtml: string;
  slideTitle: string;
  slideContent: string;
  slideNotes: string;
  slideType: string;
  slideBackgoundImage: string;
  slideBackgroudVideo: string;
  isActive: boolean;
  slideOrder: number;
  trackingCode: string;
  trackingSnippet: string;
  createdDate: string;
  createdBy: string;
  modifiedDate: string;
  modifiedBy: string;
  deletedDate: string;
  deletedBy: string;
}

export interface TextData {
  x: number;
  y: number;
  fontStyle: string;
  size: number;
  color: string;
  text: string;
  roration: number;
  alignment: string;
  font: string;
  width: number;
}

export interface SlideContent {
  background: string;
  images: Image[];
  texts: TextData[];
}

export interface SlideData {
  PitchDeckId: string;
  SlideTitle: string;
  SlideOrder: number;
  SlideContent: string;
  isActive: boolean;
}

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Hammer: any;
  }
}

declare global {
  interface Window {
    TouchEmulator: () => void;
  }
}

const SlideEditorPage = () => {
  return (
    <SlideEditorProvider>
      <SlideEditor />
    </SlideEditorProvider>
  );
};

const SlideEditor = () => {
  // const { projectData } = useSlideEditorContext();
  const [isLoading, setIsLoading] = useState(false);
  const { slideEditorDispatch, projectData, pitchDeckData } =
    useSlideEditorContext();
  const { projectId, pitchDeckId } = useParams();
  // const { user } = useContext(UserContext);
  // const { editMode } = useSlideEditorContext();
  const layerRef = useRef<Konva.Layer>(null);
  const navigate = useNavigate();
  const [initSlides, setInitSlides] = useState<SlideData[]>([]);

  useEffect(() => {
    setIsLoading(true);
    if (projectId) {
      projectService
        .getProjectByID(projectId)
        .then(data => {
          slideEditorDispatch({ type: SET_PROJECT_DATA, payload: data });

          setIsLoading(false);
        })
        .catch(error => {
          setIsLoading(false);
          if (error.response && error.response.status === 400) {
            navigate('/pages/error/400');
          }
        });
    } else {
      setIsLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    setIsLoading(true);
    if (pitchDeckId) {
      pitchDeckService
        .getPitchDeckByID(pitchDeckId)
        .then(data => {
          slideEditorDispatch({ type: SET_PITCH_DECK_DATA, payload: data });
          setIsLoading(false);
        })
        .catch(error => {
          setIsLoading(false);
          if (error.response && error.response.status === 400) {
            navigate('/pages/error/400');
          }
        });
    } else {
      setIsLoading(false);
    }
  }, [pitchDeckId, navigate]);

  const getSlidesByPitchDeckId = async () => {
    if (pitchDeckData) {
      try {
        const data = await slidesService.getAllSlideByPitchdeckId(
          pitchDeckData.pitchDeckId
        );
        return data;
      } catch (error) {
        navigate('/pages/error/400');
      }
    }
  };

  useEffect(() => {
    const handleCreateSlides = async () => {
      try {
        const data: SlidesResponse[] = await getSlidesByPitchDeckId();

        if (!data || !projectData || !pitchDeckData) return;
        const slideInfos = [
          { title: 'Cover Page', content: projectData.tagline },
          { title: 'Logline', content: projectData.logline },
          { title: 'Synopsis', content: projectData.synopsis },
          { title: 'Story and Theme', content: projectData.storyandtheme },
          {
            title: 'Characters',
            content: projectData.characters
          },
          { title: 'Locations', content: projectData.locations },
          { title: "Director's Vision", content: projectData.directorsvision },
          {
            title: 'Team',
            content: projectData.team
          },
          { title: 'Budget', content: projectData.budgets },
          { title: 'Contact', content: projectData.contact }
        ];

        const createTextData = (title: string, content: string): TextData[] => [
          {
            x: 20,
            y: 20,
            fontStyle: 'bold',
            size: 24,
            color: '#ffffff',
            text: title,
            roration: 0,
            alignment: 'center',
            font: 'Arial',
            width: window.innerWidth > 600 ? 560 : window.innerWidth - 60
          },
          {
            x: 20,
            y: 150,
            fontStyle: 'normal',
            size: 18,
            color: '#ffffff',
            text: content,
            roration: 0,
            alignment: 'center',
            font: 'Arial',
            width: window.innerWidth > 600 ? 560 : window.innerWidth - 60
          }
        ];

        const createSlide = (
          pitchDeckId: string,
          title: string,
          order: number,
          texts: TextData[]
        ): SlideData => {
          const defaultBackground =
            'https://nupitchstorage.blob.core.windows.net/nupitch-test-blob-container/nupitch_default_slide_background.jpg';

          const slideContent: SlideContent = {
            background: defaultBackground,
            images: [],
            texts
          };

          return {
            PitchDeckId: pitchDeckId,
            SlideTitle: title,
            SlideOrder: order,
            SlideContent: JSON.stringify(slideContent),
            isActive: true
          };
        };

        const initSlides: SlideData[] = slideInfos.map((slideInfo, index) =>
          createSlide(
            pitchDeckData.pitchDeckId,
            slideInfo.title,
            index + 1,
            createTextData(slideInfo.title, slideInfo.content!)
          )
        );

        if (data.length > 0) {
          //update context
          const filteredSlides = data
            .filter(slide => slide.isActive)
            .map(slide => ({
              ...JSON.parse(slide.slideContent),
              slideId: slide.slideId
            }));

          slideEditorDispatch({ type: SET_SLIDES, payload: filteredSlides });
        }
        if (data.length === 0) {
          //add initSlides and update context

          const response: SlidesResponse[] =
            await slidesService.createMultipleSlides(initSlides);
          if (response) {
            const filteredSlides = response
              .filter(slide => slide.isActive)
              .map(slide => ({
                ...JSON.parse(slide.slideContent),
                slideId: slide.slideId
              }));

            slideEditorDispatch({
              type: SET_SLIDES,
              payload: filteredSlides
            });
          }
        }
        setInitSlides(initSlides);
      } catch (error) {
        setIsLoading(false);
        navigate('/pages/error/400');
      }
    };

    handleCreateSlides();
  }, [pitchDeckData, projectData]);

  if (isLoading) {
    return <NPSpinner />;
  }

  return (
    <>
      <SlideEditorCSS />

      {/* {!editMode && user ? (
        <ProjectAnalyticsDashboard />
      ) : ( */}
      <SlideTemplateEditor
        layerRef={layerRef}
        initSlides={initSlides}
        // slideEditMode={slideEditMode}
        // activeIndex={activeIndex}
        // isLoading={isLoading}
        // setIsLoading={setIsLoading}
      />
      {/* )} */}
    </>
  );
};

export default SlideEditorPage;
